import { forwardRef } from "react";
import { Phone, Mail, MapPin, Check } from "lucide-react";
import { fmtDate as sharedFmtDate } from "@/lib/utils-format";

const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_service-platform-61/artifacts/tz4fiame_image.png";

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

// Use the shared TZ-safe formatter so YYYY-MM-DD is never shifted by UTC.
const fmtDate = (d) => sharedFmtDate(d, { long: true });

const PAYMENT_METHODS = ["Cash", "Zelle", "Check", "Credit/Debit Card", "ACH Transfer"];

function computeStatus(invoice) {
  const total = Number(invoice.total || 0);
  const deposit = Number(invoice.deposit || 0);
  const rawBalance = +(total - deposit).toFixed(2);
  const isPaid = total > 0 && rawBalance <= 0;
  const balance = rawBalance < 0 ? 0 : rawBalance;
  const credit = rawBalance < 0 ? Math.abs(rawBalance) : 0;
  return { isPaid, balance, credit };
}

const InvoicePreview = forwardRef(function InvoicePreview({ invoice }, ref) {
  const { isPaid, balance, credit } = computeStatus(invoice);

  const lineItems = invoice.line_items?.length
    ? invoice.line_items
    : [{ description: invoice.service || "Landscaping Service", qty: 1, unit_price: invoice.subtotal || 0, amount: invoice.subtotal || 0 }];

  return (
    <div
      ref={ref}
      data-invoice-doc
      className="invoice-print-area bg-white text-[#1a1a1a] font-sans relative mx-auto"
      style={{ width: "816px", minHeight: "1056px", maxWidth: "100%", boxSizing: "border-box" }}
    >
      {/* PAID IN FULL stamp — only when balance is exactly $0 */}
      {isPaid && (
        <div
          className="absolute z-0 pointer-events-none select-none"
          style={{ top: "62%", left: "50%", transform: "translate(-50%, -50%)" }}
        >
          <div
            className="font-bold uppercase tracking-[0.18em] text-center"
            style={{
              color: "#1f7a32",
              border: "5px solid #1f7a32",
              borderRadius: "10px",
              padding: "10px 38px",
              fontSize: "42px",
              opacity: 0.16,
              background: "transparent",
              fontFamily: "Outfit, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            PAID IN FULL
          </div>
        </div>
      )}

      <div className="relative z-10 p-10">
        {/* Header */}
        <div className="grid grid-cols-2 gap-6 pb-6 border-b-[3px] border-[#4a7c3c]">
          <div className="flex items-start gap-4">
            <img src={BRAND_LOGO} crossOrigin="anonymous" alt="Star Capital Landscaping" className="h-20 w-20 object-contain shrink-0" />
            <div>
              <div className="font-bold text-2xl leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                <span>STAR</span> <span className="text-[#e85c24]">CAPITAL</span>
              </div>
              <div className="text-[11px] tracking-[0.28em] text-[#4a7c3c] font-semibold mb-3">LANDSCAPING</div>
              <div className="space-y-1 text-xs text-[#444]">
                <div className="flex items-center gap-1.5"><Phone className="size-3 text-[#e85c24]"/>760-697-5023</div>
                <div className="flex items-center gap-1.5"><Mail className="size-3 text-[#e85c24]"/>starcapitallandscaping@gmail.com</div>
                <div className="flex items-center gap-1.5"><MapPin className="size-3 text-[#e85c24]"/>Austin, Texas</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-bold text-[42px] text-[#e85c24] tracking-tight leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>INVOICE</div>
            <div className="mt-4 inline-block text-left text-xs space-y-1.5 min-w-[230px]">
              <MetaRow label="Invoice #" value={invoice.number || "—"} valueClass="font-mono font-semibold" />
              <MetaRow label="Invoice Date" value={fmtDate(invoice.issue_date)} accent />
              <MetaRow label="Service Date" value={fmtDate(invoice.service_date || invoice.issue_date)} />
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="border-2 border-[#4a7c3c] rounded-md p-4">
            <div className="font-bold uppercase text-[11px] text-[#4a7c3c] tracking-wider mb-2">Bill To</div>
            <div className="text-sm font-bold">{invoice.client_name || "—"}</div>
            {invoice.address && <div className="text-xs text-[#444] mt-1 leading-relaxed">{invoice.address}</div>}
            <div className="mt-2 space-y-1 text-[11px] text-[#444]">
              {invoice.phone && <div className="flex items-center gap-1.5"><Phone className="size-3 text-[#e85c24]"/>{invoice.phone}</div>}
              {invoice.email && <div className="flex items-center gap-1.5 break-all"><Mail className="size-3 text-[#e85c24]"/>{invoice.email}</div>}
            </div>
          </div>
          <div className="border-2 border-[#1a1a1a] rounded-md p-4">
            <div className="font-bold uppercase text-[11px] text-[#1a1a1a] tracking-wider mb-2">Job Details</div>
            <Detail label="Service" value={invoice.service || "—"} />
            <Detail label="Assigned Crew" value={invoice.crew || "—"} />
            <Detail label="Service Date" value={fmtDate(invoice.service_date || invoice.issue_date)} />
            {invoice.customer_notes && <Detail label="Notes" value={invoice.customer_notes} />}
          </div>
        </div>

        {/* Service Description Table */}
        <div className="mt-6 rounded-md border-2 border-[#1a1a1a] overflow-hidden">
          <div className="grid grid-cols-[1fr_70px_120px_120px] bg-[#1a1a1a] text-white font-bold text-[11px] uppercase tracking-wider">
            <div className="p-3">Description</div>
            <div className="p-3 text-center border-l border-white/20">Qty</div>
            <div className="p-3 text-right border-l border-white/20">Unit Price</div>
            <div className="p-3 text-right border-l border-white/20">Amount</div>
          </div>
          {lineItems.map((li, idx) => (
            <div key={li._lid || `${li.description || "line"}-${idx}`} className="grid grid-cols-[1fr_70px_120px_120px] border-b border-[#4a7c3c]/30 last:border-b-0 text-sm">
              <div className="p-3 font-semibold uppercase text-[12px]">{li.description || "—"}</div>
              <div className="p-3 text-center border-l border-[#4a7c3c]/30 font-mono">{li.qty || 1}</div>
              <div className="p-3 text-right border-l border-[#4a7c3c]/30 font-mono">{fmtMoney(li.unit_price)}</div>
              <div className="p-3 text-right border-l border-[#4a7c3c]/30 font-mono">{fmtMoney(li.amount)}</div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="mt-4 flex justify-end">
          <div className="w-[320px] text-sm">
            <TotalRow label="Subtotal" value={fmtMoney(invoice.subtotal || 0)} />
            <TotalRow label="Tax" value={fmtMoney(invoice.tax || 0)} />
            {Number(invoice.discount || 0) > 0 && <TotalRow label="Discount" value={`-${fmtMoney(invoice.discount)}`} />}
            <TotalRow label="Deposit" value={`-${fmtMoney(invoice.deposit || 0)}`} />
            <div
              className="mt-2 px-4 py-3 rounded-md font-bold flex justify-between items-center"
              style={{
                background: credit > 0 ? "#4a7c3c" : "#1a1a1a",
                color: "white",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              <span className="text-[15px] uppercase tracking-wider">{credit > 0 ? "Credit Balance" : "Balance Due"}</span>
              <span className="text-lg font-mono">{credit > 0 ? fmtMoney(credit) : fmtMoney(balance)}</span>
            </div>
          </div>
        </div>

        {/* Payment Method — only show the selected one */}
        {invoice.payment_method ? (
          <div className="mt-6 border-t border-[#e5e5e5] pt-4 flex items-center gap-3 text-sm">
            <span className="font-bold uppercase tracking-wider text-[11px] text-[#4a7c3c]">Payment Method:</span>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#4a7c3c]/10 border border-[#4a7c3c]">
              <div className="size-5 rounded-full bg-[#4a7c3c] flex items-center justify-center">
                <Check className="size-3 text-white" strokeWidth={3}/>
              </div>
              <span className="font-semibold text-[#1a1a1a]">{invoice.payment_method}</span>
            </div>
          </div>
        ) : (
          <div className="mt-6 border-t border-[#e5e5e5] pt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px]">
            <span className="font-bold uppercase tracking-wider text-[#4a7c3c]">Accepted Payment Methods:</span>
            {PAYMENT_METHODS.map((m) => (
              <span key={m} className="text-[#666]">{m}</span>
            ))}
          </div>
        )}

        {invoice.notes && (
          <div className="mt-4 text-xs text-[#555] italic border-l-2 border-[#4a7c3c] pl-3">{invoice.notes}</div>
        )}

        {/* Footer */}
        <div className="mt-8 bg-[#1a1a1a] py-5 px-6 text-center rounded-md">
          <div className="grid grid-cols-3 text-white text-[10px] font-bold uppercase tracking-[0.25em] mb-2 gap-2">
            <div>Quality</div><div>Reliability</div><div>Professional Service</div>
          </div>
          <div className="italic text-2xl text-[#e85c24]" style={{ fontFamily: "Outfit, sans-serif" }}>Thank you</div>
          <div className="text-white text-[11px] italic">for choosing Star Capital Landscaping!</div>
        </div>
      </div>
    </div>
  );
});

function MetaRow({ label, value, accent, valueClass = "" }) {
  return (
    <div className="grid grid-cols-2 gap-x-4">
      <span className={`font-bold uppercase tracking-wider ${accent ? "text-[#4a7c3c]" : "text-[#1a1a1a]"}`}>{label}</span>
      <span className={`text-right ${valueClass}`}>{value}</span>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="text-xs flex gap-2 mb-0.5">
      <span className="font-bold uppercase tracking-wider text-[#1a1a1a] min-w-[80px]">{label}:</span>
      <span className="text-[#444]">{value}</span>
    </div>
  );
}

function TotalRow({ label, value }) {
  return (
    <div className="grid grid-cols-2 px-4 py-1.5 border-b border-[#e5e5e5] last:border-b-0">
      <span className="font-semibold uppercase text-[11px] tracking-wider text-[#444]">{label}</span>
      <span className="text-right font-mono">{value}</span>
    </div>
  );
}

export { computeStatus };
export default InvoicePreview;
